import React, { useMemo, useState } from "react";
import TrendChart from "./TrendChart";
import { TREND_METRICS, buildTrendTable } from "./statsChart";

const METRIC_IDS = ["occ", "rev", "res"];
const BUTTON_LABELS = { occ: "Ocupación", rev: "Ingresos", res: null };

const COMPARISON_NAMES = { yoy: "Año pasado", prev: "Período anterior" };

/**
 * "Evolución" panel shared by the court and the Beauty statistics: the trend of one metric
 * (occupancy, revenue or the booking count) for this period against the comparison period, with a
 * metric toggle, a legend and a "Ver tabla" twin.
 *
 * @param {{current: object, comparison: object|null, compareMode: string, bucket: number,
 *   buildSeries: (daily: Array, metric: string, bucket: number) => Array, metrics?: object,
 *   countButton: string}} props
 *   `buildSeries` is the screen's series builder (occ / rev / res); `metrics` its metric table
 *   (labels and formats); `countButton` the label of the third toggle ("Reservas", "Turnos").
 *   `bucket` groups days (1 = daily, 7 = weekly average) to keep long ranges readable.
 */
export default function TrendPanel({ current, comparison, compareMode, bucket, buildSeries, metrics = TREND_METRICS, countButton }) {
  const [metric, setMetric] = useState("occ");
  const [view, setView] = useState("chart");

  const currentSeries = useMemo(() => buildSeries(current.daily, metric, bucket), [buildSeries, current, metric, bucket]);
  const comparisonSeries = useMemo(
    () => (comparison ? buildSeries(comparison.daily, metric, bucket) : null),
    [buildSeries, comparison, metric, bucket]
  );
  const comparisonName = comparison ? COMPARISON_NAMES[compareMode] || "Período anterior" : "";

  const label = metrics[metric].label;
  const subtitle =
    metric === "occ"
      ? `${label} ${bucket === 1 ? "de cada día" : "de cada semana"}`
      : `${label}, ${bucket === 1 ? "por día" : "promedio diario por semana"}`;

  const table = useMemo(
    () => buildTrendTable({ current: currentSeries, comparison: comparisonSeries, bucket, metric, comparisonName, metrics }),
    [currentSeries, comparisonSeries, bucket, metric, comparisonName, metrics]
  );

  return (
    <section className="stats-panel" aria-labelledby="stats-trend-title">
      <div className="stats-panel__head">
        <div>
          <h2 className="stats-panel__title" id="stats-trend-title">
            Evolución
          </h2>
          <p className="stats-panel__sub">{subtitle}</p>
        </div>
        <div className="stats-panel__tools">
          <div className="stats-seg" role="group" aria-label="Métrica del gráfico">
            {METRIC_IDS.map((id) => (
              <button key={id} type="button" aria-pressed={metric === id} onClick={() => setMetric(id)}>
                {BUTTON_LABELS[id] || countButton}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="stats-ghost-btn"
            aria-pressed={view === "table"}
            onClick={() => setView((v) => (v === "chart" ? "table" : "chart"))}
          >
            {view === "chart" ? "Ver tabla" : "Ver gráfico"}
          </button>
        </div>
      </div>

      <div className="stats-legend">
        <span>
          <i className="stats-key-line" style={{ background: "var(--stats-current)" }} />
          Este período
        </span>
        {comparisonSeries && (
          <span>
            <i className="stats-key-line" style={{ background: "var(--stats-compare)" }} />
            {comparisonName}
          </span>
        )}
      </div>

      {view === "chart" ? (
        <TrendChart
          current={currentSeries}
          comparison={comparisonSeries}
          metric={metric}
          bucket={bucket}
          comparisonName={comparisonName}
          metrics={metrics}
        />
      ) : (
        <div className="stats-table-wrap">
          <table>
            <thead>
              <tr>
                {table.headers.map((header, index) => (
                  <th key={header} className={index % 2 ? "num" : undefined}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, index) => (
                    <td key={index} className={index % 2 ? "num" : undefined}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
